import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_CATEGORY_CATALOG, GET_SMART_INBOX_INTEGRATIONS } from '../../../customer/queries.ts';
import { darkenHex, getCategoryOutlookTag } from '../../../customer/pages/SmartInbox/customCategoryMapping.ts';
import { OUTLOOK_CATEGORY_COLORS } from '../../../customer/pages/SmartInbox/CreateCustomCategoryModal.tsx';
import useAgentMailboxLinks from './useAgentMailboxLinks.ts';

export interface CategoryCatalogFilters {
    search: string;
    automationFilter: string[];
    statusFilter: string[];
    mailboxFilter: string[];
}
export interface MailboxOption {
    id: string;
    address: string;
}
export interface CategoryCatalogRow {
    id: string;
    name: string;
    enabled: boolean;
    outlookTagName: string | null;
    outlookTagHex: string | undefined;
    outlookTagTextHex: string | undefined;
    mailboxId: string;
    mailboxAddress: string;
    automationName: string | null;
    raw: any;
}

interface UseCategoryCatalogResult {
    rows: CategoryCatalogRow[];
    totalCount: number;
    loading: boolean;
    agentOptions: { value: string; label: string }[];
    mailboxOptions: MailboxOption[];
    refetch: () => Promise<any>;
}

export const useCategoryCatalog = (
    page: number,
    perPage: number,
    filters: CategoryCatalogFilters
): UseCategoryCatalogResult => {
    const {
        data: catalogData,
        loading: catalogLoading,
        refetch,
    } = useQuery(GET_CATEGORY_CATALOG, {
        variables: {
            page,
            perPage,
            search: filters.search || undefined,
            automation: filters.automationFilter.length ? filters.automationFilter : undefined,
            status: filters.statusFilter.length ? filters.statusFilter : undefined,
            mailboxId: filters.mailboxFilter.length ? filters.mailboxFilter : undefined,
        },
        fetchPolicy: 'cache-and-network',
    });
    const { agentNameByMailboxId, agentOptions, loading: agentsLoading } = useAgentMailboxLinks();
    const { data: mailboxData } = useQuery(GET_SMART_INBOX_INTEGRATIONS, { fetchPolicy: 'cache-first' });

    const rows: CategoryCatalogRow[] = useMemo(() => {
        return (catalogData?.categoryCatalog?.items || []).map((item: any) => {
            const outlookTag = getCategoryOutlookTag(item);
            const outlookTagHex = outlookTag?.colorName
                ? OUTLOOK_CATEGORY_COLORS.find((c) => c.name === outlookTag.colorName)?.hex
                : undefined;

            return {
                id: item.id,
                name: item.name,
                enabled: item.enabled,
                outlookTagName: outlookTag?.name ?? null,
                outlookTagHex,
                outlookTagTextHex: outlookTagHex ? darkenHex(outlookTagHex) : undefined,
                mailboxId: item.mailbox?.id ?? '',
                mailboxAddress: item.mailbox?.mailInbox ?? '',
                automationName:
                    item.sourceType === 'AGENT' ? (agentNameByMailboxId.get(item.mailbox?.id) ?? null) : null,
                raw: item,
            };
        });
    }, [catalogData, agentNameByMailboxId]);

    const mailboxOptions: MailboxOption[] = useMemo(
        () => (mailboxData?.smartInboxIntegrations || []).map((m: any) => ({ id: m.id, address: m.mailInbox })),
        [mailboxData]
    );
    return {
        rows,
        totalCount: catalogData?.categoryCatalog?.totalCount ?? 0,
        loading: catalogLoading || agentsLoading,
        agentOptions,
        mailboxOptions,
        refetch,
    };
};

export default useCategoryCatalog;
